import { useQuery } from '@tanstack/react-query';
import { Check, Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import type { StorefrontBundle } from '@/features/storefront/api/storefront';
import { useQuickCart } from '@/features/storefront/components/QuickCart';
import { StorefrontImage } from '@/features/storefront/components/StorefrontImage';
import { useShopContent } from '@/features/storefront/content/useContent';
import { useStorefrontData, useStorefrontScope } from '@/features/storefront/data/context';
import { useStorefrontFeatures } from '@/features/storefront/features';
import { useAddToCart } from '@/features/storefront/hooks/useCart';
import { bundleSaving } from '@/features/storefront/parts/bundleSaving';
import { usePartsEnvironment, useProductParts } from '@/features/storefront/parts/context';
import type { ProductPartProps } from '@/features/storefront/parts/ProductParts';
import { partOwnsSpacing } from '@/features/storefront/parts/style';
import { useStorefrontPaths } from '@/features/storefront/paths';
import { partChoice, partFlag } from '@/features/storefront/sections/parts';
import { STOREFRONT_HEADING_STYLE } from '@/features/storefront/theme';
import { cn } from '@/lib/cn';
import { formatMoney } from '@/lib/money';

type BundleItem = StorefrontBundle['items'][number];

/**
 * "Frequently bought together": this product and the ones the merchant bundled
 * with it, joined with +, the set's price and one button that adds them all.
 *
 * Draws nothing when the product has no bundle, or nothing of it is in stock -
 * the server leaves those out. The discount comes off in the cart, recomputed
 * there, and only while the whole set is in it.
 */
export function ProductBundlePart({ part }: ProductPartProps) {
  const text = useShopContent();
  const product = useProductParts();
  const source = useStorefrontData();
  const scope = useStorefrontScope();
  const features = useStorefrontFeatures();
  const editor = usePartsEnvironment()?.includeHidden ?? false;
  const quickCart = useQuickCart();
  const paths = useStorefrontPaths();
  const navigate = useNavigate();
  const addToCart = useAddToCart();

  const slug = product?.detail.slug ?? '';
  const asks = !editor && Boolean(slug) && Boolean(source.productBundle);
  const query = useQuery({
    queryKey: [...scope, 'product-bundle', slug],
    queryFn: () => source.productBundle?.(slug) ?? Promise.resolve(null),
    enabled: asks,
    staleTime: 60_000,
  });

  // The anchor follows the variant the shopper picked, while it is in stock.
  const items: BundleItem[] = useMemo(() => {
    const bundle = query.data;
    if (!bundle || !product) return [];
    const selected = product.selected;
    return bundle.items.map((item) =>
      item.anchor && selected?.in_stock
        ? { ...item, variant_id: selected.id, price: selected.price }
        : item,
    );
  }, [query.data, product]);

  const [unticked, setUnticked] = useState<string[]>([]);
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!added) return;
    const timer = window.setTimeout(() => setAdded(false), 2500);
    return () => window.clearTimeout(timer);
  }, [added]);

  if (!product) return null;

  const heading = part.label.trim() || text('product_page.bundle_heading');
  const spacing = partOwnsSpacing(part) ? 'mt-8' : undefined;

  if (editor) {
    // The customiser is not the shop's host, so it cannot ask for the set;
    // it shows the shape instead, with the product and two empty places.
    return (
      <section className={cn(spacing, 'text-sm')} data-testid="product-bundle-preview">
        <h2 className="text-lg font-semibold" style={STOREFRONT_HEADING_STYLE}>
          {heading}
        </h2>
        <div className="mt-3 flex items-center gap-2">
          <Tile name={product.detail.name} image={product.detail.images[0]?.url ?? null} />
          {[0, 1].map((index) => (
            <span key={index} className="flex items-center gap-2">
              <Plus className="size-4 shrink-0" aria-hidden />
              <Tile name="" image={null} />
            </span>
          ))}
        </div>
      </section>
    );
  }

  const bundle = query.data;
  if (!features.cart || !bundle || items.length < 2) return null;

  const showChecks = partFlag(part, 'show_checkboxes');
  const layout = partChoice(part, 'layout') === 'list' ? 'list' : 'row';
  const chosen = showChecks ? items.filter((item) => !unticked.includes(item.product_id)) : items;
  const allChosen = chosen.length === items.length;
  const base = chosen.reduce((sum, item) => sum + Number(item.price), 0);
  const saving = allChosen ? bundleSaving(bundle, base) : 0;
  const total = Math.max(0, base - saving);
  const offersDiscount = bundleSaving(bundle, 1000) > 0;

  const toggle = (id: string) =>
    setUnticked((current) => (current.includes(id) ? current.filter((one) => one !== id) : [...current, id]));

  const addAll = async () => {
    if (chosen.length === 0 || adding) return;
    setAdding(true);
    setFailed(false);
    try {
      // One at a time: each adds one more to whatever is already in the
      // basket, and the last answer is the cart every page then reads.
      for (const item of chosen) {
        await addToCart.mutateAsync({ variantId: item.variant_id, quantity: 1 });
      }
      setAdded(true);
      if (quickCart.openOnAdd) quickCart.openCart();
      else if (!quickCart.enabled) void navigate(paths.cart());
    } catch {
      setFailed(true);
    } finally {
      setAdding(false);
    }
  };

  const label = (item: BundleItem) =>
    item.anchor ? `${text('product_page.bundle_this_item')}: ${item.name}` : item.name;

  return (
    <section className={cn(spacing, 'text-sm')} aria-label={heading} data-testid="product-bundle">
      <h2 className="text-lg font-semibold" style={STOREFRONT_HEADING_STYLE}>
        {heading}
      </h2>

      {layout === 'row' ? (
        <div className="mt-3 flex flex-wrap items-center gap-2" aria-hidden>
          {items.map((item, index) => (
            <span key={item.product_id} className="flex items-center gap-2">
              {index > 0 ? <Plus className="size-4 shrink-0" /> : null}
              <Tile
                name={item.name}
                image={item.image_url}
                dimmed={showChecks && unticked.includes(item.product_id)}
              />
            </span>
          ))}
        </div>
      ) : null}

      <ul className="mt-3 space-y-2">
        {items.map((item) => {
          const ticked = !showChecks || !unticked.includes(item.product_id);
          const row = (
            <>
              {layout === 'list' ? <Tile name={item.name} image={item.image_url} small dimmed={!ticked} /> : null}
              <span className="min-w-0 flex-1" style={ticked ? undefined : { color: 'var(--storefront-muted)' }}>
                {label(item)}
              </span>
              <span className="shrink-0 font-semibold">{formatMoney(item.price)}</span>
            </>
          );
          return (
            <li key={item.product_id}>
              {showChecks ? (
                <label className="flex cursor-pointer items-center gap-3">
                  <input
                    type="checkbox"
                    className="size-4 shrink-0"
                    checked={ticked}
                    onChange={() => toggle(item.product_id)}
                    style={{ accentColor: 'var(--storefront-primary)' }}
                  />
                  {row}
                </label>
              ) : (
                <div className="flex items-center gap-3">{row}</div>
              )}
            </li>
          );
        })}
      </ul>

      <div className="mt-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p>
            <span style={{ color: 'var(--storefront-muted)' }}>{text('product_page.bundle_total')}: </span>
            <span className="text-base font-semibold" data-testid="bundle-total">
              {formatMoney(total)}
            </span>
            {saving > 0 ? (
              <span className="ms-2 line-through" style={{ color: 'var(--storefront-muted)' }}>
                {formatMoney(base)}
              </span>
            ) : null}
          </p>
          {saving > 0 ? (
            <p className="mt-0.5 font-semibold" style={{ color: 'var(--storefront-primary)' }} data-testid="bundle-saving">
              {text('product_page.bundle_save', { amount: formatMoney(saving) })}
            </p>
          ) : offersDiscount && !allChosen ? (
            <p className="mt-0.5" style={{ color: 'var(--storefront-muted)' }}>
              {text('product_page.bundle_unlock', { count: items.length })}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => void addAll()}
          disabled={adding || chosen.length === 0}
          className="h-11 px-5 text-sm font-semibold text-white disabled:opacity-60"
          style={{ backgroundColor: 'var(--storefront-primary)', borderRadius: 'var(--storefront-radius)' }}
        >
          {added ? (
            <span className="inline-flex items-center gap-1.5">
              <Check className="size-4" aria-hidden />
              {text('product_page.bundle_added')}
            </span>
          ) : allChosen ? (
            text('product_page.bundle_add_all')
          ) : (
            text('product_page.bundle_add_selected')
          )}
        </button>
      </div>
      {failed ? (
        <p role="alert" className="mt-2 text-xs" style={{ color: 'var(--storefront-primary)' }}>
          {text('common.error')}
        </p>
      ) : null}
    </section>
  );
}

function Tile({
  name,
  image,
  small = false,
  dimmed = false,
}: {
  name: string;
  image: string | null;
  small?: boolean;
  dimmed?: boolean;
}) {
  return (
    <span
      className={cn('block shrink-0 overflow-hidden border', small ? 'size-12' : 'size-20 sm:size-24')}
      style={{
        borderColor: 'var(--storefront-surface)',
        borderRadius: 'var(--storefront-radius)',
        backgroundColor: 'var(--storefront-surface)',
        opacity: dimmed ? 0.4 : 1,
      }}
    >
      {image ? <StorefrontImage url={image} alt={name} className="size-full object-cover" /> : null}
    </span>
  );
}
